/**
 * The Jobs board — `design/mockups/03-jobs-board/jobs-board-main.png`.
 *
 * It is the same list engine as Contacts and Properties, which is the point of
 * §3.5. What is specific to jobs is here and nowhere else: the seven metric
 * cards (which are saved filters, §6.3), the four revenue columns, the
 * computed Next action, the status stripe, and the bulk bar.
 *
 * Next action is computed on the client from inputs the server selects, using
 * the same `/shared/next-action.ts` rules the API answers a job detail with.
 * Two copies of that logic would drift the first time a threshold changed.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { JOB_STATUSES, type MeResponse } from '@rowted/shared'
import { ListScreen } from '../components/list-screen.js'
import type { BulkAction } from '../components/bulk-menu.js'
import { StatusChip, humanize } from '../components/primitives.js'
import { ConfirmDialog, Toast } from '../components/overlays.js'
import { RowTray } from '../components/row-tray.js'
import { jobMark } from '../components/landing-marks.js'
import { jobStatusChip, lastActivity, text } from '../components/card-line.js'
import { useList, type ListRow } from '../lib/use-list.js'
import { openItems, copyItem } from '../lib/row-menu.js'
import type { CardLine, MenuItem } from '../components/data-list.js'
import { formatCompact, formatNumber } from '../lib/format.js'
import { request } from '../lib/api.js'
import { IconCheck, IconLayers } from '../frame/icons.js'
import { NewSplitButton } from '../requests/new-split-button.js'
import { cn } from '../lib/cn.js'
import { usePhone } from '../lib/use-narrow.js'

/**
 * The board reads these whether or not the Fields menu is showing them.
 *
 * `nextAction` is a real column now, but its urgency is what paints the Age
 * cell red and what the "n need action" count counts, so the flag is fetched
 * even when the column itself is hidden.
 */
const NEXT_ACTION_INPUTS = [
  'status', 'ageInStatusDays', 'nextActionUrgent', 'reworkOpen', 'reworkReason',
  'visitsDone', 'visitsPlanned', 'isOverdue', 'dueOn', 'quoteSentDaysAgo',
  'invoiced', 'expected',
  // Read but not shown as a column: it is the second line under the job title,
  // and dropping the column would otherwise drop the fetch with it.
  'reference',
  // The row menu opens the property behind the job.
  'propertyId',
  /*
   * What the mark draws (`docs/list-marks.md` §1). Fetched here rather than
   * left to the Fields menu, because a mark that disappears when somebody
   * hides a column is a column of holes.
   */
  'serviceLine', 'divisionIcon', 'divisionColour', 'jobType',
  /* The card's second and third rows — see `jobCardLine`. */
  'propertyName', 'orgUnitName', 'lastActivityAt',
]

/** Whether this job is the company being late — the red text and the count. */
const urgent = (row: ListRow): boolean => Boolean(row.nextActionUrgent)

/**
 * Whether a row's ⋯ offers **Cancel job**: every job but one that is settled
 * or already cancelled.
 *
 * Declined is not settled. `job-stages.md` §2 lets Cancel job take a Declined
 * job (*Declined → Cancelled*; Board 1 draws Cancel job in a declined job's
 * ⋯), so the row offers it too.
 */
export const cancellableFromList = (status: string): boolean =>
  !['paid', 'cancelled', 'written_off'].includes(status)


export function JobsBoard({ me, onNew, onNewRequest, reloadSignal }: {
  me: MeResponse
  onNew?: () => void
  /**
   * New request beside the page's own New (`new-request.md` §13.1), absent
   * for somebody who can't take one — then the plain button stays.
   */
  onNewRequest?: () => void
  /** Bumped after New request creates something, so the list shows it (§11.4). */
  reloadSignal?: number
}) {
  const list = useList('jobs', {
    alwaysFetch: NEXT_ACTION_INPUTS,
    /*
     * §6.3 drew the board grouped by status. It opens ungrouped: with a real
     * pipeline the first page is spent on collapsed headers reading "showing 0
     * of 170", and grouping by status is one click from the gear when you want
     * it. The subtotals stay — they are the footer as much as the group rows.
     */
    defaultView: {
      aggregates: [
        { field: 'expected', fn: 'sum' },
        { field: 'earned', fn: 'sum' },
        { field: 'invoiced', fn: 'sum' },
        { field: 'received', fn: 'sum' },
      ],
    },
  })
  /* The list is the page's, so the page reloads it — the engine is not changed (§11.4). */
  useEffect(() => { if (reloadSignal) list.reload() }, [reloadSignal])
  const navigate = useNavigate()
  const phone = usePhone()
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [cancelling, setCancelling] = useState<ListRow | null>(null)
  const [cancelReason, setCancelReason] = useState('')

  const canEdit = me.permissions.jobs === 'edit' || me.permissions.jobs === 'full'
  const needAction = list.rows.filter(urgent).length

  /**
   * The row's ⋯ menu, led by what the job needs next (v2: "then the record's
   * Next-action verb as the highlighted first action").
   *
   * The verb comes from the same rules the Next action column draws, so the
   * menu and the column cannot disagree about what a job wants. Where the verb
   * is something one click can finish — reviewing a request — it finishes it;
   * everywhere else it opens the job, which is where quoting, scheduling and
   * invoicing actually happen. A menu item that claims to invoice and then
   * opens a page is a menu item that lied.
   */
  function rowMenu(row: ListRow): MenuItem[] {
    const action = String(row.nextAction ?? '')
    const status = String(row.status ?? '')
    const items: MenuItem[] = openItems(`/jobs/${row.id}`, navigate)

    if (action) {
      // The one verb a menu can finish on the spot. Everything else opens the
      // job, which is where quoting, scheduling and invoicing happen.
      const reviewable = canEdit && status === 'requested'
      items.push({
        key: 'next',
        label: reviewable ? 'Mark reviewed' : action,
        tone: 'primary',
        separatorBefore: true,
        onSelect: () => (reviewable
          ? void runBulk([row], 'mark_reviewed')
          : navigate(`/jobs/${row.id}`)),
      })
    }

    if (row.propertyId) {
      items.push({
        key: 'property',
        label: 'Open the property',
        separatorBefore: !action,
        onSelect: () => navigate(`/properties/${String(row.propertyId)}`),
      })
    }

    items.push(...copyItem('copy-reference', 'Copy job number', row.reference))

    // Cancelling is the destructive one, and the state machine will not take it
    // once a job is settled.
    if (canEdit && cancellableFromList(status)) {
      items.push({
        key: 'cancel',
        label: 'Cancel job',
        tone: 'danger',
        separatorBefore: true,
        onSelect: () => { setCancelReason(''); setCancelling(row) },
      })
    }

    return items
  }

  async function cancelJob(row: ListRow, reason: string) {
    setBusy(true)
    try {
      await request(`/jobs/${row.id}/transition`,
        { method: 'POST', body: { to: 'cancelled', reason } })
      list.reload()
      setNotice(`${String(row.reference ?? 'The job')} cancelled.`)
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : 'That did not work.')
    } finally {
      setBusy(false)
      setCancelling(null)
    }
  }

  async function runBulk(
    rows: ListRow[], action: string, extra: Record<string, unknown> = {},
  ) {
    setBusy(true)
    try {
      const result = await request<{ moved: number; failed: { message: string }[] }>(
        '/jobs/bulk',
        { method: 'POST', body: { jobIds: rows.map((row) => row.id), action, ...extra } })
      list.reload()
      setNotice(result.failed.length === 0
        ? `${formatNumber(result.moved)} updated.`
        : `${formatNumber(result.moved)} updated · ${result.failed.length} could not be: `
          + `${result.failed[0]!.message}`)
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : 'That did not work.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <ListScreen
        area="jobs"
        title="Jobs"
        list={list}
        actions={onNew || onNewRequest
          ? (
            <NewSplitButton label="New job" icon={IconLayers} sub="a job at a property you already have"
              phoneSub="a job at a property you already have" onNew={onNew} onNewRequest={onNewRequest} />
          )
          : undefined}
        /*
         * The division's tile with a status dot, replacing the 4px colour line
         * this board used to draw — the one mark in the app that said nothing
         * about *what* the row is (`docs/list-marks.md` §1).
         */
        mark={{ render: jobMark }}
        /* The tray is the four things somebody expands a job row to check. */
        tray={(row) => (
          <RowTray
            area="jobs"
            id={row.id}
            actions={[
              { key: 'open', label: 'Open job', primary: true,
                onSelect: () => navigate(`/jobs/${row.id}`) },
              ...(row.propertyId
                ? [{
                  key: 'property',
                  label: 'Open the property',
                  onSelect: () => navigate(`/properties/${String(row.propertyId)}`),
                }]
                : []),
            ]}
          />
        )}
        rowMenu={rowMenu}
        // DataList draws the count beside the label, so this is the name only.
        groupLabel={(key) => humanize(key)}
        columnWidths={{
          reference: 84,
          status: 104,
          nextAction: 170,
          customerName: 140,
          ageInStatusDays: 52,
        }}
        primaryColumn={{
          field: 'title',
          header: 'Job',
          width: 240,
          /*
           * **Which job, and where** — the reference and the property.
           *
           * The reference alone identified the row and said nothing about
           * it; the property is the first thing anybody asks about a job
           * they do not recognise by name.
           */
          render: (row) => ({
            primary: String(row.title ?? ''),
            secondary: [row.reference, row.propertyName].filter(Boolean).join(' · ') || undefined,
          }),
        }}
        cardLine={jobCardLine}
        cell={{
          status: (row) => (
            <StatusChip group="job" status={String(row.status ?? 'requested')}>
              {humanize(String(row.status ?? ''))}
            </StatusChip>
          ),
          ageInStatusDays: (row) => (
            <span className={
              urgent(row)
                ? 'tnum rounded-tag bg-danger-bg px-1.5 text-[11px] font-semibold text-danger'
                : 'tnum text-text-muted'
            }>
              {Number(row.ageInStatusDays ?? 0)}d
            </span>
          ),
          nextAction: (row) => {
            const text = String(row.nextAction ?? '')
            if (!text) return '—'
            return (
              <span className={urgent(row) ? 'font-semibold text-danger' : 'text-text'}>
                {text}
              </span>
            )
          },
          marginPct: (row) => {
            const margin = row.marginPct
            if (margin === null || margin === undefined) return '—'
            const value = Number(margin)
            // Thin margins are the ones worth noticing on a board.
            return (
              <span className={value < 30 ? 'tnum text-warn' : 'tnum text-text'}>{value}%</span>
            )
          },
        }}
        bulk={canEdit ? { actions: JOB_BULK(runBulk) } : undefined}
        onRowClick={(row) => navigate(`/jobs/${row.id}`)}
        emptyTitle="No jobs yet"
        emptyBody="A job is one piece of work for one property. Every quote, visit and invoice hangs off one."
        emptyAction={onNew ? { label: 'New job', onClick: onNew } : undefined}
        importOrgUnitId={me.scope.selected[0]}
      />

      {/*
        * §6.3's footer: "19 need action", and the lifecycle legend.
        *
        * Not on a phone. It is a key to the colours of a stripe somebody can
        * read straight off the chip on every card, and at 390px it wraps into
        * four lines — a quarter of the screen spent restating what the cards
        * already say, directly above the bar that actually does something.
        */}
      <div className={cn(
        'flex-wrap items-center gap-x-4 gap-y-1 border-t border-line px-4 py-2',
        'text-[11px] text-text-muted',
        phone ? 'hidden' : 'flex',
      )}>
        <span className="font-semibold text-text">
          {formatNumber(list.visibleCount)} jobs in view
        </span>
        {needAction > 0 && (
          <span className="font-semibold text-danger">{needAction} need action</span>
        )}
        <span className="ml-auto flex flex-wrap items-center gap-2">
          <span className="text-label">Lifecycle</span>
          {JOB_STATUSES.filter((status) =>
            !['declined', 'cancelled', 'written_off'].includes(status)).map((status) => (
            <span key={status} className="flex items-center gap-1">
              <span
                aria-hidden
                className="h-1.5 w-1.5 rounded-full"
                style={{ background: `rgb(var(--status-job-${status}-fg))` }}
              />
              {humanize(status)}
            </span>
          ))}
        </span>
      </div>

      {/*
        * Cancelling asks for a reason rather than only a confirmation, because
        * §4.4 requires one on this transition and the audit row is where "why
        * did this job go away?" gets answered six months later.
        */}
      {cancelling && (
        <ConfirmDialog
          title={`Cancel ${String(cancelling.title ?? 'this job')}?`}
          body={
            <>
              <p className="m-0">
                The job stays on the books as cancelled, with everything already on it.
                Say why — it goes on the record.
              </p>
              <input
                autoFocus
                value={cancelReason}
                onChange={(event) => setCancelReason(event.target.value)}
                placeholder="Customer went with someone else"
                className="mt-2 h-8 w-full rounded-field border border-line bg-surface px-2 text-cell text-text"
              />
            </>
          }
          confirmLabel="Cancel job"
          busyLabel="Cancelling…"
          busy={busy}
          disabled={cancelReason.trim().length === 0}
          onCancel={() => setCancelling(null)}
          onConfirm={() => void cancelJob(cancelling, cancelReason.trim())}
        />
      )}

      {notice && (
        <div className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2">
          <Toast onDismiss={() => setNotice(null)}>{notice}</Toast>
        </div>
      )}
    </>
  )
}

/**
 * What the Jobs board can do to a selection (v3 §3.3).
 *
 * These still go through `/jobs/bulk`, which has its own verbs and its own
 * savepoints and predates the engine's `/bulk-actions/:key`. The spec is
 * explicit that migrating it is a later PR, so they are declared here as
 * `local` actions: the **menu** is the engine's — same groups, same order,
 * same confirm — and only the request underneath is still the board's.
 *
 * **No status moves** (`job-stages.md` §2). *Move to Quoted* is gone: a job is
 * Quoted when one of its quotes is sent or marked quoted, and nothing else
 * moves Requested, Quoted, Approved, Scheduled or Declined — the server
 * refuses such a move row by row (`use_the_action`), which comes back through
 * the engine's skipped reasons like any other refusal.
 */
export const JOB_BULK = (
  run: (rows: ListRow[], action: string, extra?: Record<string, unknown>) => Promise<void>,
): BulkAction[] => [
  {
    key: 'mark_reviewed',
    group: 'change',
    label: () => 'Mark reviewed',
    icon: IconCheck,
    local: (ctx) => run(ctx.rows, 'mark_reviewed'),
  },
]

/**
 * A job's third row: **[Quoted] $12,400 · Harrisburg — Sep 26** (v3 §8).
 *
 * `expected` is what the job is worth — §4.6's first of the four revenue
 * numbers, which is what "total" means before anything has been invoiced.
 * It is drawn `formatCompact`, so a six-figure job reads as `$124k` rather
 * than taking the row's whole width away from the branch beside it.
 *
 * `$0` is dropped rather than printed: a job in Requested has no quote yet,
 * and a nought there reads as "this one is worth nothing" rather than as
 * "nobody has priced it".
 */
function jobCardLine(row: ListRow): CardLine {
  const expected = Number(row.expected ?? 0)
  return {
    chip: jobStatusChip(row),
    facts: [
      expected > 0 ? formatCompact(expected, 'money') : null,
      text(row.orgUnitName) || null,
    ].filter((part) => part !== null),
    trailing: lastActivity(row),
  }
}
